import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "@tanstack/react-router";
import QRCode from "qrcode";
import type { LoginResult, Me } from "shared";
import { api, ApiError, setCsrf } from "../api";
import { useAuth } from "../auth";
import { Footer } from "../components/Footer";

export function Login() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<"password" | "totp" | "enroll">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [qr, setQr] = useState<{ img: string; secret: string } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (me) return <Navigate to="/" />;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(e instanceof ApiError ? e.message : "Something went wrong"); } finally { setBusy(false); }
  };

  const submitPassword = (e: FormEvent) => { e.preventDefault(); run(async () => {
    const r = await api<LoginResult>("/api/auth/login", { method: "POST", body: { email, password } });
    setCsrf(r.csrf);
    if (r.next === "enroll" && r.otpauthUri) {
      setQr({ img: await QRCode.toDataURL(r.otpauthUri), secret: new URL(r.otpauthUri).searchParams.get("secret") ?? "" });
    }
    setStep(r.next === "enroll" ? "enroll" : "totp");
  }); };

  const submitCode = (e: FormEvent) => { e.preventDefault(); run(async () => {
    const m = await api<Me>("/api/auth/totp", { method: "POST", body: { code } });
    setCsrf(m.csrf);
    await refresh();
    navigate({ to: "/" });
  }); };

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="card w-full max-w-sm">
          <h1 className="mb-1 text-lg font-semibold">Sign in</h1>
          <p className="mb-4 text-sm text-slate-500">Ask For Helping Hand rep portal</p>
          {error && <p role="alert" className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {step === "password" ? (
            <form onSubmit={submitPassword} className="flex flex-col gap-3">
              <div><label className="label" htmlFor="email">Email</label>
                <input id="email" className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></div>
              <div><label className="label" htmlFor="password">Password</label>
                <input id="password" className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></div>
              <button className="btn-primary justify-center" disabled={busy}>Continue</button>
            </form>
          ) : (
            <form onSubmit={submitCode} className="flex flex-col gap-3">
              {step === "enroll" && qr && (
                <div className="text-sm">
                  <p className="mb-2">Set up two-step sign-in: scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password), then enter the 6-digit code.</p>
                  <img src={qr.img} alt="QR code for your authenticator app" className="mx-auto h-44 w-44" />
                  <p className="mt-2 break-all text-center font-mono text-xs text-slate-500">{qr.secret}</p>
                </div>
              )}
              <div><label className="label" htmlFor="code">6-digit code</label>
                <input id="code" className="input tracking-widest" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus /></div>
              <button className="btn-primary justify-center" disabled={busy}>Sign in</button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
